import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { useSettingsStore } from "@/stores/storeSettings";
import { useCookieConsent } from "@/composables/useCookieConsent";
import { canShowEventAnnouncement, dismissEventAnnouncement } from "@/utils/eventAnnouncement";

const SHOW_DELAY_MS = 2500;

const isParkGorkogoRoute = (routeName) => String(routeName || "").includes("parkgorkogo");

export const useEventAnnouncement = () => {
	const route = useRoute();
	const storeSettings = useSettingsStore();
	const { isConsentGiven, isConsentVisible } = useCookieConsent();

	const isOpen = ref(false);
	let showTimerId = null;

	const hasActiveEvent = computed(() => Boolean(storeSettings.settings?.event));

	const isAllowedRoute = computed(() => {
		const routeName = String(route.name || "");

		if (routeName.includes("telegram")) {
			return false;
		}

		if (isParkGorkogoRoute(routeName)) {
			return false;
		}

		return true;
	});

	const canSchedulePopup = computed(() => {
		return (
			isConsentGiven.value &&
			!isConsentVisible.value &&
			hasActiveEvent.value &&
			isAllowedRoute.value &&
			canShowEventAnnouncement()
		);
	});

	const clearShowTimer = () => {
		if (showTimerId) {
			clearTimeout(showTimerId);
			showTimerId = null;
		}
	};

	const hidePopup = () => {
		clearShowTimer();
		isOpen.value = false;
	};

	const dismissPopup = () => {
		dismissEventAnnouncement();
		hidePopup();
	};

	const openPopup = () => {
		if (!canShowEventAnnouncement() || isOpen.value) {
			return;
		}

		isOpen.value = true;
	};

	const schedulePopup = () => {
		clearShowTimer();

		if (!canSchedulePopup.value || isOpen.value) {
			return;
		}

		showTimerId = setTimeout(() => {
			openPopup();
		}, SHOW_DELAY_MS);
	};

	const closePopup = () => {
		dismissPopup();
	};

	watch(
		() => route.name,
		(routeName) => {
			if (isParkGorkogoRoute(routeName)) {
				dismissPopup();
				return;
			}

			if (isOpen.value) {
				return;
			}

			schedulePopup();
		},
		{ immediate: true }
	);

	watch(canSchedulePopup, (canShow) => {
		if (canShow && !isOpen.value) {
			schedulePopup();
		}
	});

	watch(isConsentVisible, (visible) => {
		if (visible) {
			clearShowTimer();

			if (!isOpen.value) {
				return;
			}
		} else if (canSchedulePopup.value && !isOpen.value) {
			schedulePopup();
		}
	});

	onBeforeUnmount(() => {
		clearShowTimer();
	});

	return {
		isOpen,
		closePopup,
	};
};
